/**
 * useDraftAutosave — persistance localStorage générique et debouncée.
 *
 * Sert à éviter la perte de saisie utilisateur sur crash/refresh/fermeture
 * d'onglet (Phase 2 prod-readiness DooQuest : un répondant qui remplit un long
 * formulaire perdait toutes ses réponses si le navigateur fermait avant l'envoi).
 *
 * Pattern aligné sur l'autosave créateur (FormPollCreator.tsx) : debounce 800ms,
 * échec silencieux loggé en warn. `load()` lit le brouillon (à appeler une fois
 * au montage), `clear()` le supprime (à appeler après soumission réussie).
 *
 * Générique : ne connaît rien du domaine, sérialise n'importe quelle valeur JSON.
 */

import { useCallback, useEffect, useRef } from "react";
import { logger } from "@core/lib/logger";

export interface DraftAutosaveApi<T> {
  /** Lit le brouillon persisté (ou null si absent/illisible). */
  load: () => T | null;
  /** Supprime le brouillon (après soumission réussie). */
  clear: () => void;
}

export interface UseDraftAutosaveOptions {
  /** Désactive l'écriture (ex: après soumission, ou poll clôturé). Défaut: true. */
  enabled?: boolean;
  /** Délai de debounce en ms. Défaut: 800 (aligné FormPollCreator). */
  debounceMs?: number;
}

export function useDraftAutosave<T>(
  key: string,
  value: T,
  options: UseDraftAutosaveOptions = {},
): DraftAutosaveApi<T> {
  const { enabled = true, debounceMs = 800 } = options;
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      try {
        window.localStorage.setItem(key, JSON.stringify({ v: value }));
      } catch (e) {
        logger.warn("Draft autosave failed", "poll", e);
      }
    }, debounceMs);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [key, value, enabled, debounceMs]);

  const load = useCallback((): T | null => {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { v?: T } | null;
      return (parsed?.v ?? null) as T | null;
    } catch {
      return null;
    }
  }, [key]);

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* localStorage indisponible — rien à nettoyer */
    }
  }, [key]);

  return { load, clear };
}
